import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { HelpCircle, Hospital, PieChart, BedDouble } from "lucide-react";
import { hasGuideFor } from "@/pages/Help";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { useTheme } from "@/context/ThemeContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Pill, Boxes, ShoppingCart, ArrowLeftRight, Users2,
  Building2, Settings, ScrollText, ShieldCheck, LogOut, ChevronDown,
  Sun, Moon, Truck, ClipboardList, Undo2, HeartPulse, ShieldPlus, ReceiptText, Bike, FileText, MapPin, ClipboardCheck, CalendarClock, BarChart3, SlidersHorizontal, Map, Database, PackageX, Menu, X, Warehouse, Clock, BookOpen,
} from "lucide-react";

function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    o.start();
    o.stop(ctx.currentTime + 0.45);
    o.onended = () => ctx.close();
  } catch (e) { /* audio not available */ }
}

const NAV = [
  { group: "Operations", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view", end: true },
    { to: "/analytics", label: "Analytics", icon: BarChart3, perm: "analytics.view" },
    { to: "/reports", label: "Reports", icon: Database, perms: ["reports.all", "reports.branch", "reports.delivery"] },
    { to: "/missed-sales", label: "Out-of-stock demand", icon: PackageX, perm: "missed_sales.view" },
    { to: "/pos", label: "Point of sale", icon: ShoppingCart, perm: "pos.sell" },
    { to: "/orders", label: "Sales & invoices", icon: ScrollText, perm: "sales.view" },
    { to: "/proforma", label: "Proforma quotes", icon: FileText, perm: "proforma.view" },
    { to: "/customer-returns", label: "Customer returns", icon: Undo2, perm: "sales.returns" },
    { to: "/shift-oversight", label: "Shift oversight", icon: Clock, perm: "shifts.manage" },
    { to: "/inpatients", label: "Inpatients", icon: BedDouble, perm: "inpatients.view" },
  ]},
  { group: "Inventory", items: [
    { to: "/products", label: "Products", icon: Pill, perm: "products.view" },
    { to: "/inventory", label: "Stock & batches", icon: Boxes, perm: "inventory.view" },
    { to: "/transfers", label: "Transfers", icon: ArrowLeftRight, perm: "transfers.view" },
    { to: "/stocktake", label: "Stocktaking", icon: ClipboardCheck, perm: "stocktake.view" },
    { to: "/adjustments", label: "Stock adjustments", icon: SlidersHorizontal, perm: "adjustments.view" },
    { to: "/warehouse-daily", label: "Warehouse daily", icon: Warehouse, perm: "warehouse_daily.view" },
    { to: "/near-expiry", label: "Near-expiry", icon: CalendarClock, perm: "near_expiry.view" },
    { to: "/ledger", label: "Movement ledger", icon: ScrollText, perm: "ledger.view" },
  ]},
  { group: "Procurement", items: [
    { to: "/suppliers", label: "Suppliers", icon: Truck, perm: "suppliers.view" },
    { to: "/procurement", label: "Purchase orders", icon: ClipboardList, perm: "procurement.view" },
    { to: "/returns", label: "Returns", icon: Undo2, perm: "procurement.returns" },
  ]},
  { group: "Partner Supply", items: [
    { to: "/wholesale", label: "Partner supply & hospital partners", icon: Hospital, perms: ["wholesale.view", "wholesale.orders", "wholesale.accounts", "wholesale.fulfil"] },
    { to: "/wholesale-reports", label: "Partner supply reports", icon: PieChart, perm: "wholesale.reports" },
  ]},
  { group: "Insurance", items: [
    { to: "/claims", label: "Claims", icon: HeartPulse, perm: "finance.claims" },
    { to: "/schemes", label: "Schemes", icon: ShieldPlus, perm: "schemes.view" },
  ]},
  { group: "Delivery", items: [
    { to: "/deliveries", label: "Deliveries", icon: Bike, perm: "delivery.view" },
    { to: "/live-map", label: "Live map", icon: Map, perm: "livemap.view" },
    { to: "/rider", label: "My deliveries", icon: MapPin, perm: "delivery.rider" },
    { to: "/prescriptions", label: "Prescriptions", icon: FileText, perm: "prescriptions.view" },
  ]},
  { group: "Customers", items: [
    { to: "/customers", label: "Customers", icon: Users2, perm: "customers.view" },
    { to: "/statements", label: "Corporate statements", icon: ReceiptText, perm: "finance.statements" },
  ]},
  { group: "Administration", items: [
    { to: "/users", label: "Users & roles", icon: ShieldCheck, perm: "users.view" },
    { to: "/branches", label: "Branches", icon: Building2, perm: "branches.manage" },
    { to: "/settings", label: "Business rules", icon: Settings, perm: "settings.manage" },
    { to: "/audit", label: "Audit trail", icon: ScrollText, perm: "audit.view" },
  ]},
  { group: "Support", items: [
    { to: "/help", label: "Help & guides", icon: BookOpen, perm: "help.view" },
  ]},
];

const allowed = (i, can) => (i.perms ? i.perms.some((p) => can(p)) : can(i.perm));
export const firstAllowedPath = (can) => NAV.flatMap((s) => s.items).find((i) => allowed(i, can))?.to;

export default function Layout() {
  const { user, logout, can } = useAuth();
  const { branches, allBranches, activeBranch, changeBranch, restricted } = useBranch();
  const { theme, toggle } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    if (!user?.id || !can("help.view")) return;
    if (!localStorage.getItem(`mm_help_tour_${user.id}`)) setTourOpen(true);
  }, [user, can]);
  const dismissTour = (go) => {
    if (user?.id) localStorage.setItem(`mm_help_tour_${user.id}`, "1");
    setTourOpen(false);
    if (go) navigate("/help");
  };
  const [pendingRx, setPendingRx] = useState(0);
  const prevRx = useRef(null);
  const [dueStocktakes, setDueStocktakes] = useState(0);
  const [refillsDue, setRefillsDue] = useState(0);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const prevAppr = useRef(null);
  useEffect(() => {
    if (!can("prescriptions.view")) return;
    let alive = true;
    const load = () => api.get("/prescriptions", { params: { status: "pending" } })
      .then((r) => {
        if (!alive) return;
        const n = r.data.length;
        if (prevRx.current !== null && n > prevRx.current) {
          const added = n - prevRx.current;
          toast(`New prescription received`, { description: `${added} new · ${n} awaiting review` });
          playChime();
        }
        prevRx.current = n;
        setPendingRx(n);
      }).catch(() => {});
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [can]);
  useEffect(() => {
    if (!can("stocktake.view")) return;
    let alive = true;
    const load = () => api.get("/stocktakes", { params: { status: "open" } })
      .then((r) => { if (alive) setDueStocktakes(r.data.filter((s) => s.auto).length); }).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [can]);
  useEffect(() => {
    if (!can("prescriptions.view")) return;
    let alive = true;
    const today = new Date().toISOString().slice(0, 10);
    const load = () => api.get("/prescriptions", { params: { status: "verified" } })
      .then((r) => { if (alive) setRefillsDue(r.data.filter((x) => (x.refills_remaining || 0) > 0 && x.next_refill_due && x.next_refill_due <= today).length); }).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [can]);

  useEffect(() => {
    if (!can("inventory.transfer") || !can("transfers.view")) return;
    let alive = true;
    const isSuper = (user?.permissions || []).includes("*");
    const myB = user?.branch_ids || [];
    const load = () => api.get("/transfers")
      .then((r) => {
        if (!alive) return;
        const mine = r.data.filter((t) => t.status === "pending_approval" && (isSuper || myB.includes(t.from_branch_id)));
        const n = mine.length;
        if (prevAppr.current !== null && n > prevAppr.current) {
          toast("Transfer resolution needs your approval", { description: `${n - prevAppr.current} new · ${n} awaiting your approval` });
          playChime();
        }
        prevAppr.current = n;
        setPendingApprovals(n);
      }).catch(() => {});
    load();
    const iv = setInterval(load, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [can, user]);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />
      )}
      {/* Sidebar */}
      <aside
        className={`w-56 shrink-0 flex flex-col fixed inset-y-0 left-0 overflow-y-auto z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ background: "var(--mm-nav)", color: "var(--mm-nav-text)" }}
      >
        <div className="px-3 py-4 border-b flex justify-center relative" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <div className="rounded-xl bg-white px-3 py-2 shadow-sm w-full flex justify-center">
            <img src="/mobimed-logo.png" alt="MobiMed Pharmacy" className="h-16 object-contain" />
          </div>
          <button type="button" onClick={() => setSidebarOpen(false)} data-testid="sidebar-close-btn" aria-label="Close menu" className="lg:hidden absolute right-3 top-3 text-white/70 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Branch switcher */}
        <div className="px-3 py-4 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1.5" style={{ color: "var(--mm-nav-text)" }}>Location</label>
          <Select value={activeBranch} onValueChange={changeBranch}>
            <SelectTrigger data-testid="branch-switcher-select" className="h-10 text-white border-white/20 bg-white/10 focus:ring-white/40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {!restricted && <SelectItem value="all">All locations (HQ)</SelectItem>}
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <nav className="flex-1 px-2.5 py-4 space-y-5">
          {NAV.map((sec) => {
            const items = sec.items.filter((i) => allowed(i, can));
            if (!items.length) return null;
            return (
              <div key={sec.group}>
                <div className="px-2 text-[10px] font-semibold uppercase tracking-wider mb-1.5 opacity-70">{sec.group}</div>
                <div className="space-y-0.5">
                  {items.map((i) => (
                    <NavLink
                      key={i.to}
                      to={i.to}
                      end={i.end}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${i.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-sm transition-colors ${isActive ? "text-white font-semibold" : "hover:bg-white/5"}`
                      }
                      style={({ isActive }) => isActive ? { background: "var(--mm-nav-selected)" } : undefined}
                    >
                      <i.icon className="h-4 w-4" />
                      <span className="flex-1">{i.label}</span>
                      {i.to === "/prescriptions" && pendingRx > 0 && (
                        <span data-testid="rx-pending-badge" className="text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-rose-500 text-white">{pendingRx}</span>
                      )}
                      {i.to === "/prescriptions" && refillsDue > 0 && (
                        <span data-testid="rx-refill-badge" title="Refills due" className="text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-amber-500 text-white">{refillsDue}</span>
                      )}
                      {i.to === "/stocktake" && dueStocktakes > 0 && (
                        <span data-testid="stocktake-due-badge" className="text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-amber-500 text-white">{dueStocktakes}</span>
                      )}
                      {i.to === "/transfers" && pendingApprovals > 0 && (
                        <span data-testid="transfer-approval-badge" title="Resolutions awaiting your approval" className="text-[10px] font-bold rounded-full px-1.5 py-0.5 bg-sky-500 text-white">{pendingApprovals}</span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-56 flex flex-col min-h-screen">
        <header
          className="h-14 flex items-center justify-between px-6 sticky top-0 z-20 border-b"
          style={{ background: "var(--mm-surface)", borderColor: "var(--mm-border)" }}
        >
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" data-testid="sidebar-toggle-btn" aria-label="Open menu" onClick={() => setSidebarOpen(true)} className="lg:hidden h-9 w-9">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="text-sm" style={{ color: "var(--mm-text-2)" }}>
              {activeBranch === "all" ? "All locations" : allBranches.find((b) => b.id === activeBranch)?.name}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {can("help.view") && hasGuideFor(location.pathname, can) && (
              <Button variant="ghost" size="icon" data-testid="page-help-btn" title="Help for this page" aria-label="Help for this page" onClick={() => navigate(`/help?for=${location.pathname.slice(1)}`)} className="h-9 w-9">
                <HelpCircle className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" data-testid="theme-toggle-btn" onClick={toggle} aria-label="Toggle theme" className="h-9 w-9">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" data-testid="user-menu-btn" className="gap-2 h-9">
                  <div className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: "var(--mm-surface-raised)", color: "var(--mm-action)" }}>
                    {(user?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                  <div className="text-left leading-tight hidden sm:block">
                    <div className="text-sm font-medium text-foreground">{user?.name}</div>
                    <div className="text-[11px] text-muted-foreground">{user?.role_name}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} style={{ color: "var(--mm-error)" }}>
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 md:p-8">
          <Outlet />
        </main>
      </div>

      <Dialog open={tourOpen} onOpenChange={(o) => { if (!o) dismissTour(false); }}>
        <DialogContent className="max-w-md" data-testid="welcome-tour-dialog">
          <DialogHeader>
            <DialogTitle>👋 Welcome, {user?.name?.split(" ")[0]}!</DialogTitle>
            <DialogDescription>
              New here? We’ve put simple, step-by-step guides for your everyday tasks in one place.
              Find them any time under <span className="font-medium text-foreground">Support → Help &amp; guides</span> in the menu.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="tour-later-btn" onClick={() => dismissTour(false)}>Maybe later</Button>
            <Button data-testid="tour-show-btn" onClick={() => dismissTour(true)}>Show me the guides</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
